// @mui material components
import {
  Card,
  Fade,
  Grid,
  Icon,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Skeleton,
  Tooltip,
} from "@mui/material";
import PropTypes from "prop-types";

// Kaizen Dashboard components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import { useContext, useEffect, useState } from "react";
import dayjs from "dayjs";
import SoftButton from "components/SoftButton";
import { gql, useMutation, useQuery } from "@apollo/client";
import { formatMoneyPunto } from "utils/formatters";
import { MessageManager } from "context";
import { UserContext } from "context/user";
import { getColor } from "utils/colors";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "30%", xxl: "30%" },
  overflow: "auto",
};

export default function ModalNuevoPermisoCaza({ open, handleClose, type, handleFinish }) {
  const [fechaPermiso, setFechaPermiso] = useState(null);
  const [medioDePago, setMedioDePago] = useState(null);
  const { handleSnackbar } = useContext(MessageManager);

  const { data: precios } = useQuery(gql`
    query getPrecios {
      preciosPermisoCaza {
        cazaMayorResidente
        cazaMayorNoResidente
        cazaMenorResidente
        cazaMenorNoResidente
        cazaMayorMenorResidente
        cazaMayorMenorNoResidente
        acompananteCaza
      }
    }
  `);

  const { data: usuario } = useQuery(gql`
    query getUser {
      currentUser {
        id
        provincia {
          id
        }
      }
    }
  `);

  const [addPermiso] = useMutation(gql`
    mutation addPermiso($input: PermisoGobInput!) {
      addPermisoGob(input: $input) {
        id
      }
    }
  `);

  useEffect(() => {
    if (open) {
      setFechaPermiso(null);
      setMedioDePago(null);
    }
  }, [open]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox display="flex" justifyContent="space-between" alignItems="center" p={3} pb={0}>
            <SoftTypography variant="h6">{`Nuevo ${type?.toLowerCase()}`}</SoftTypography>
            <SoftBox display="flex" justifyContent="end" alignItems="center">
              <SoftBox display="flex" justifyContent="end" alignItems="center">
                <Tooltip title="Cerrar" placement="top">
                  <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                    close
                  </Icon>
                </Tooltip>
              </SoftBox>
            </SoftBox>
          </SoftBox>
          <SoftBox p={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <SoftBox display="flex" flexDirection="column" gap={1}>
                  <SoftTypography variant="h7">
                    El permiso solicitado tiene validez hasta el último día del año
                  </SoftTypography>
                  <SoftTypography variant="h7">
                    Deberá presentar la siguiente documentación:
                  </SoftTypography>
                  <SoftTypography variant="h7" fontWeight="bold">
                    - Documento de identidad
                  </SoftTypography>
                  <SoftTypography variant="h7" fontWeight="bold">
                    - Carnet ANMaC
                  </SoftTypography>
                  <SoftBox display="flex" gap={1}>
                    <SoftTypography variant="h7" fontWeight="bold">
                      - Permiso de campo (Firmado por policia o escribano)
                    </SoftTypography>
                    <Tooltip title="Descargar modelo" placement="top">
                      <Icon
                        fontSize="small"
                        sx={{
                          cursor: "pointer",
                          color: getColor("gobSecondary"),
                        }}
                        onClick={() => {
                          window.open(
                            "https://permisosdepesca.sanluis.gov.ar/Permiso_Due%C3%B1o_del_Campo.pdf",
                            "_blank"
                          );
                        }}
                      >
                        download
                      </Icon>
                    </Tooltip>
                  </SoftBox>
                </SoftBox>
              </Grid>
              <Grid item xs={12}>
                <InputLabel htmlFor="fechaPermiso">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Año del permiso
                  </SoftTypography>
                </InputLabel>
                <Select
                  name="fechaPermiso"
                  value={fechaPermiso || ""}
                  onChange={(e) => {
                    setFechaPermiso(e.target.value);
                  }}
                >
                  <MenuItem value={dayjs().format("YYYY")}>{dayjs().format("YYYY")}</MenuItem>
                </Select>
              </Grid>
              <Grid item xs={12}>
                <InputLabel htmlFor="medioDePago">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Medio de pago
                  </SoftTypography>
                </InputLabel>
                <Select
                  name="medioDePago"
                  value={medioDePago || ""}
                  onChange={(e) => {
                    setMedioDePago(e.target.value);
                  }}
                >
                  <MenuItem value={"Volante Electrónico de Pago"}>
                    Volante Electrónico de Pago
                  </MenuItem>
                  <MenuItem value={"Transferencia Bancaria"}>Transferencia Bancaria</MenuItem>
                </Select>
              </Grid>
              <Grid item xs={12} display="flex" justifyContent="space-between">
                <SoftTypography variant="h6">Precio del permiso</SoftTypography>
                {usuario?.currentUser?.provincia?.id ? (
                  <SoftTypography variant="h6">{`$${formatMoneyPunto(
                    type === "Permiso de caza mayor"
                      ? usuario?.currentUser?.provincia?.id === "20"
                        ? precios?.preciosPermisoCaza?.cazaMayorResidente
                        : precios?.preciosPermisoCaza?.cazaMayorNoResidente
                      : type === "Permiso de caza menor"
                      ? usuario?.currentUser?.provincia?.id === "20"
                        ? precios?.preciosPermisoCaza?.cazaMenorResidente
                        : precios?.preciosPermisoCaza?.cazaMenorNoResidente
                      : type === "Permiso de caza mayor y menor"
                      ? usuario?.currentUser?.provincia?.id === "20"
                        ? precios?.preciosPermisoCaza?.cazaMayorMenorResidente
                        : precios?.preciosPermisoCaza?.cazaMayorMenorNoResidente
                      : 0
                  )} ARS`}</SoftTypography>
                ) : (
                  <Skeleton variant="text" width={100} />
                )}
              </Grid>
              <Grid item xs={12}>
                <SoftButton
                  color="primary"
                  onClick={() => {
                    addPermiso({
                      variables: {
                        input: {
                          fechaInicio: fechaPermiso,
                          tipo: type,
                          medioDePago,
                          monto:
                            type === "Permiso de caza mayor"
                              ? usuario?.currentUser?.provincia?.id === "20"
                                ? precios?.preciosPermisoCaza?.cazaMayorResidente
                                : precios?.preciosPermisoCaza?.cazaMayorNoResidente
                              : type === "Permiso de caza menor"
                              ? usuario?.currentUser?.provincia?.id === "20"
                                ? precios?.preciosPermisoCaza?.cazaMenorResidente
                                : precios?.preciosPermisoCaza?.cazaMenorNoResidente
                              : type === "Permiso de caza mayor y menor"
                              ? usuario?.currentUser?.provincia?.id === "20"
                                ? precios?.preciosPermisoCaza?.cazaMayorMenorResidente
                                : precios?.preciosPermisoCaza?.cazaMayorMenorNoResidente
                              : 0,
                        },
                      },
                    })
                      .then((res) => {
                        handleSnackbar("Permiso creado correctamente", "success");
                        handleFinish(res.data.addPermisoGob.id);
                        handleClose();
                      })
                      .catch((e) => {
                        handleSnackbar(
                          e.message || "Ha ocurrido un error al crear el permiso",
                          "error"
                        );
                      });
                  }}
                  fullWidth
                  disabled={
                    !fechaPermiso ||
                    !medioDePago ||
                    (type === "diario" &&
                      fechaPermiso &&
                      dayjs(fechaPermiso).isBefore(dayjs().add(1, "day").format("YYYY-MM-DD")))
                  }
                >
                  Guardar e ir a pagar&nbsp;
                  <Icon>arrow_forward</Icon>
                </SoftButton>
              </Grid>
            </Grid>
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalNuevoPermisoCaza.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  handleFinish: PropTypes.func,
};
